/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Icon,
  Typography,
  useTheme,
  UtilityButton,
  Loader,
  useForm,
  Input,
} from '@quickbit/qb-design-system';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUpdate, useSearch, ThemeOverride } from '../../useSitesApi';
import { ColorInput } from './components';
import { themeValidators } from './themeValidators';

const defaultTheme: ThemeOverride = {
  content: {
    companyName: 'New Site' + Math.random().toString(36).substring(7),
  },
  theme: {
    core: {
      palette: {
        background: undefined,
        primary: {
          x50: undefined,
          x200: undefined,
          default: undefined,
          x700: undefined,
        },
        secondary: {
          x50: undefined,
          x200: undefined,
          default: undefined,
          x700: undefined,
        },
        border: undefined,
        grey: {
          x50: undefined,
          x200: undefined,
          default: undefined,
          x700: undefined,
        },
        black: undefined,
      },
      shape: {
        borderRadius: undefined,
        buttonRadius: undefined,
      },
      typography: {
        fontFamily: undefined,
      },
    },
    fontSrc: undefined,
  },
};

const mergeThemes = (defaultTheme: ThemeOverride, newTheme: ThemeOverride) => {
  return {
    ...defaultTheme,
    ...newTheme,
    theme: {
      ...defaultTheme.theme,
      ...newTheme.theme,
      core: {
        ...defaultTheme.theme?.core,
        ...newTheme.theme?.core,
        palette: {
          ...defaultTheme.theme?.core?.palette,
          ...newTheme.theme?.core?.palette,
          primary: {
            ...defaultTheme.theme?.core?.palette?.primary,
            ...newTheme.theme?.core?.palette?.primary,
          },
          secondary: {
            ...defaultTheme.theme?.core?.palette?.secondary,
            ...newTheme.theme?.core?.palette?.secondary,
          },
          grey: {
            ...defaultTheme.theme?.core?.palette?.grey,
            ...newTheme.theme?.core?.palette?.grey,
          },
        },
        shape: {
          ...defaultTheme.theme?.core?.shape,
          ...newTheme.theme?.core?.shape,
        },
        typography: {
          ...defaultTheme.theme?.core?.typography,
          ...newTheme.theme?.core?.typography,
        },
      },
    },
  };
};

export const SiteDetails = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const { domain } = useParams();

  const { fields, setters, errors, hasErrors } = useForm(
    defaultTheme,
    themeValidators
  );
  const { data, isLoading } = useSearch(
    { domain: domain || '' },
    { page: 1, size: 1 }
  );

  const { mutate, isPending: isUpdating } = useUpdate();

  const site = useMemo(() => data?.sites[0], [data]);

  useEffect(() => {
    if (!site) return;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const parsedTheme = JSON.parse(site.theme);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const merged = mergeThemes(defaultTheme, parsedTheme);
    setters.set(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: spacing.xxs,
            alignItems: 'center',
          }}
        >
          <div
            css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
          >
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/new-skinning/sites')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                Skinning Sites
              </Typography>
            </div>
            <div
              css={{
                transform: 'rotate(180deg)',
                svg: {
                  fill: 'none',
                  stroke: palette.grey.x300,
                },
              }}
            >
              <Icon name="chevron" />
            </div>
            <Typography variant="subtitle">{domain}</Typography>
          </div>
          <UtilityButton
            loading={isLoading || isUpdating}
            text="Update"
            onClick={() => {
              // convert all empty string values to undefined
              const updatedFields = JSON.parse(
                JSON.stringify(fields),
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                (_, value) => (value === '' ? undefined : value)
              ) as ThemeOverride;
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              mutate({ domain: domain!, theme: JSON.stringify(updatedFields) });
            }}
            disabled={hasErrors}
          />
        </div>
      </Header>
      <Card>
        {isLoading && <Loader />}
        <div css={{ display: 'grid', gap: spacing.xxs }}>
          <div>
            <Typography variant="subtitle">Content</Typography>
            <Input
              label="Company Name"
              value={fields.content.companyName || ''}
              onChange={(e) => setters.content.companyName.set(e.target.value)}
            />
          </div>
          <div>
            <Typography variant="subtitle">Palette</Typography>
            <div
              css={{
                display: 'grid',
                gap: spacing.xxs,
                gridTemplateColumns: 'repeat(3, 1fr)',
              }}
            >
              <div>
                <Typography variant="bodySmallBold">Primary</Typography>
                <ColorInput
                  label="x50"
                  value={fields.theme?.core?.palette?.primary?.x50 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.primary?.x50?.set(value)
                  }
                  error={errors.theme?.core?.palette?.primary?.x50}
                />
                <ColorInput
                  label="x200"
                  value={fields.theme?.core?.palette?.primary?.x200 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.primary?.x200?.set(value)
                  }
                  error={errors.theme?.core?.palette?.primary?.x200}
                />
                <ColorInput
                  label="default"
                  value={fields.theme?.core?.palette?.primary?.default || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.primary?.default?.set(value)
                  }
                  error={errors.theme?.core?.palette?.primary?.default}
                />
                <ColorInput
                  label="x700"
                  value={fields.theme?.core?.palette?.primary?.x700 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.primary?.x700?.set(value)
                  }
                  error={errors.theme?.core?.palette?.primary?.x700}
                />
              </div>
              <div>
                <Typography variant="bodySmallBold">Secondary</Typography>
                <ColorInput
                  label="x50"
                  value={fields.theme?.core?.palette?.secondary?.x50 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.secondary?.x50?.set(value)
                  }
                  error={errors.theme?.core?.palette?.secondary?.x50}
                />
                <ColorInput
                  label="x200"
                  value={fields.theme?.core?.palette?.secondary?.x200 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.secondary?.x200?.set(value)
                  }
                  error={errors.theme?.core?.palette?.secondary?.x200}
                />
                <ColorInput
                  label="default"
                  value={fields.theme?.core?.palette?.secondary?.default || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.secondary?.default?.set(value)
                  }
                  error={errors.theme?.core?.palette?.secondary?.default}
                />
                <ColorInput
                  label="x700"
                  value={fields.theme?.core?.palette?.secondary?.x700 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.secondary?.x700?.set(value)
                  }
                  error={errors.theme?.core?.palette?.secondary?.x700}
                />
              </div>
              <div>
                <Typography variant="bodySmallBold">Grey</Typography>
                <ColorInput
                  label="x50"
                  value={fields.theme?.core?.palette?.grey?.x50 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.grey?.x50?.set(value)
                  }
                  error={errors.theme?.core?.palette?.grey?.x50}
                />
                <ColorInput
                  label="x200"
                  value={fields.theme?.core?.palette?.grey?.x200 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.grey?.x200?.set(value)
                  }
                  error={errors.theme?.core?.palette?.grey?.x200}
                />
                <ColorInput
                  label="default"
                  value={fields.theme?.core?.palette?.grey?.default || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.grey?.default?.set(value)
                  }
                  error={errors.theme?.core?.palette?.grey?.default}
                />
                <ColorInput
                  label="x700"
                  value={fields.theme?.core?.palette?.grey?.x700 || ''}
                  onChange={(value) =>
                    setters.theme?.core?.palette?.grey?.x700?.set(value)
                  }
                  error={errors.theme?.core?.palette?.grey?.x700}
                />
              </div>
              <ColorInput
                label="Border"
                value={fields.theme?.core?.palette?.border || ''}
                onChange={(value) =>
                  setters.theme?.core?.palette?.border?.set(value)
                }
                error={errors.theme?.core?.palette?.border}
              />
              <ColorInput
                label="Background"
                value={fields.theme?.core?.palette?.background || ''}
                onChange={(value) =>
                  setters.theme?.core?.palette?.background?.set(value)
                }
                error={errors.theme?.core?.palette?.background}
              />
              <ColorInput
                label="Black"
                value={fields.theme?.core?.palette?.black || ''}
                onChange={(value) =>
                  setters.theme?.core?.palette?.black?.set(value)
                }
                error={errors.theme?.core?.palette?.black}
              />
            </div>
          </div>
          <div>
            <Typography variant="subtitle">Shape</Typography>
            <div
              css={{
                display: 'grid',
                gap: spacing.xxs,
                gridTemplateColumns: 'repeat(2, 1fr)',
              }}
            >
              <Input
                label="Border Radius"
                value={fields.theme?.core?.shape?.borderRadius || ''}
                onChange={(e) =>
                  setters.theme?.core?.shape?.borderRadius?.set(e.target.value)
                }
                error={!!errors.theme?.core?.shape?.borderRadius}
                helperText={errors.theme?.core?.shape?.borderRadius || ''}
              />
              <Input
                label="Button Radius"
                value={fields.theme?.core?.shape?.buttonRadius || ''}
                onChange={(e) =>
                  setters.theme?.core?.shape?.buttonRadius?.set(e.target.value)
                }
                error={!!errors.theme?.core?.shape?.buttonRadius}
                helperText={errors.theme?.core?.shape?.buttonRadius || ''}
              />
            </div>
          </div>
          <div>
            <Typography variant="subtitle">Typography</Typography>
            <div
              css={{
                display: 'grid',
                gap: spacing.xxs,
                gridTemplateColumns: 'repeat(2, 1fr)',
              }}
            >
              <Input
                label="Font Family"
                value={fields.theme?.core?.typography?.fontFamily || ''}
                onChange={(e) =>
                  setters.theme?.core?.typography?.fontFamily?.set(
                    e.target.value
                  )
                }
              />
              <Input
                label="Font Src"
                value={fields.theme?.fontSrc || ''}
                onChange={(e) => setters.theme?.fontSrc?.set(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};
